import React from 'react';

import * as FileUtil from 'folder-items/FileUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import * as net from 'util/Net';
import {Optional} from 'util/Types';

enum SequenceFormat {
  FORMAT_FASTA = 'FORMAT_FASTA',
  FORMAT_GENBANK = 'FORMAT_GENBANK',
  FORMAT_RDF = 'FORMAT_RDF',
}

interface FormatItem {
  name: string;
  handleOnClick: () => void;
}

export interface ExportSequenceModalProps {
  editURL: string;
  onRequestClose: () => void;
  dropdownDisplayString?: string;
}

interface State {
  format: Optional<string>;
  nocol: boolean;
  restrictFeatType: boolean;
}

class ExportSequenceModal extends React.Component<ExportSequenceModalProps, State> {
  static formatToText: {[format: string]: string} = {
    FORMAT_GENBANK: 'Genbank (.gb)',
    FORMAT_FASTA: 'FASTA (.fasta)',
    FORMAT_RDF: 'SBOL RDF (.rdf)',
  };

  state: State = {
    format: null,
    nocol: true,
    restrictFeatType: false,
  };

  _getFormatItems = (): Array<FormatItem> =>
    Object.keys(ExportSequenceModal.formatToText).map((format) => ({
      name: ExportSequenceModal.formatToText[format],
      handleOnClick: () => {
        this.setState({format});
      },
    }));

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getExportUrl = () => {
    switch (this.state.format) {
      case SequenceFormat.FORMAT_FASTA:
        return FileUtil.getExportURL(this.props.editURL, 'fasta', {dl: true});
      case SequenceFormat.FORMAT_RDF:
        return FileUtil.getExportURL(this.props.editURL, 'rdf', {dl: true});
      case SequenceFormat.FORMAT_GENBANK: {
        return FileUtil.getExportURL(this.props.editURL, 'gb', {
          dl: true,
          nocol: this.state.nocol,
          restrictFeatType: this.state.restrictFeatType,
        });
      }
      default:
        throw new Error('Unrecognized download format.');
    }
  };

  _handleDownload = (): void => {
    net.submitCSRFForm('POST', this._getExportUrl(), {target: '_blank'});
  };

  _getDisplayName = (): string => {
    if (!this.state.format) {
      return 'Choose sequence format...';
    }
    return ExportSequenceModal.formatToText[this.state.format];
  };

  render(): JSX.Element {
    return (
      <Modal
        enableRedesign={true}
        title="Export sequence"
        onRequestClose={this.props.onRequestClose}
        medium={true}
        buttons={[
          {
            text: 'Download',
            submit: true,
            style: ButtonStyle.PRIMARY,
            disabled: !this.state.format,
            onClick: this._handleDownload,
            dataTestAttrs: {component: 'ExportSequenceModal', element: 'submit'},
          },
        ]}
        dataTestAttrs={{component: 'ExportSequenceModal', element: 'root'}}
      >
        <div className="form-group">
          <SelectDropdown
            displayName={this._getDisplayName()}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                {this._getFormatItems().map((formatItem) => (
                  <Option key={formatItem.name} onSelect={formatItem.handleOnClick}>
                    {formatItem.name}
                  </Option>
                ))}
              </Menu>
            }
            dataTestAttrs={{component: 'ExportSequenceModal', element: 'dropdown'}}
          />
        </div>
        {this.state.format === SequenceFormat.FORMAT_GENBANK && [
          <div className="checkbox" key="no-col">
            <label>
              <input
                type="checkbox"
                checked={!this.state.nocol}
                onChange={(e) => {
                  this.setState({nocol: !this.state.nocol});
                }}
              />
              Export feature colors
            </label>
          </div>,
          <div className="checkbox" key="feat-type">
            <label>
              <input
                type="checkbox"
                className="u-small-spacing-right"
                checked={this.state.restrictFeatType}
                onChange={(e) => {
                  this.setState({restrictFeatType: !this.state.restrictFeatType});
                }}
              />
              Convert non-standard annotation types
            </label>
          </div>,
        ]}
      </Modal>
    );
  }
}

export default ExportSequenceModal;
